import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import type { PlanExecutionReport } from '../../../../../models/PlanExecutionTypes';
import type { StockPlan, StockPlanResult } from '../../../../../models/StockPickTypes';
import {
  getPlanResult,
  savePlanProfitOverride,
} from '../../../../../services/request/CapitalManagement';
import { formatCurrency } from '../../../../../utils/CurrencyFormat';

interface Props {
  plan: StockPlan;
  report: PlanExecutionReport | null;
  onSaved?: () => void;
}

const PlanProfitOverrideSection: React.FC<Props> = ({ plan, report, onSaved }) => {
  const [result, setResult] = useState<StockPlanResult | null>(null);
  const [overrideInput, setOverrideInput] = useState('');
  const [saving, setSaving] = useState(false);

  const calculatedFromSells = report?.realizedGainBeforeTax ?? result?.calculatedProfitAmount ?? null;

  useEffect(() => {
    if (!plan.id) return;
    getPlanResult(plan.id)
      .then((r) => {
        setResult(r);
        if (r?.profitOverride != null) {
          setOverrideInput(String(r.profitOverride));
        } else {
          setOverrideInput('');
        }
      })
      .catch(() => setResult(plan.result ?? null));
  }, [plan.id, plan.result]);

  const persistOverride = async (value: number | null) => {
    if (!plan.id) return;
    setSaving(true);
    try {
      const updated = await savePlanProfitOverride(plan.id, value);
      setResult(updated);
      toast.success(value == null ? 'Using calculated profit from sells' : 'Profit override saved');
      onSaved?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOverride = async () => {
    const value = overrideInput.trim() === '' ? null : Number(overrideInput);
    if (value != null && Number.isNaN(value)) {
      toast.error('Enter a valid profit amount or leave blank to use calculated');
      return;
    }
    await persistOverride(value);
  };

  const handleClearOverride = async () => {
    setOverrideInput('');
    await persistOverride(null);
  };

  if (!report && calculatedFromSells == null) {
    return null;
  }

  const effective = result?.profitAmount;
  const hasOverride = result?.profitOverride != null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Plan profit (sells only)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Calculated only from recorded sells (lot matching vs purchase). Not shown to partners. You may
        override the plan profit for internal reporting.
      </Typography>

      <Stack spacing={1.5}>
        <Typography variant="body2">
          <strong>Calculated from sells:</strong>{' '}
          {calculatedFromSells != null ? formatCurrency(calculatedFromSells) : '—'}
          {report && report.sharesSold === 0 && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (no sells yet)
            </Typography>
          )}
        </Typography>
        {effective != null && (
          <Typography variant="body2">
            <strong>Effective profit:</strong> {formatCurrency(effective)}
            {hasOverride && (
              <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                (override active)
              </Typography>
            )}
          </Typography>
        )}
        <TextField
          label="Profit override (INR)"
          type="number"
          size="small"
          value={overrideInput}
          onChange={(e) => setOverrideInput(e.target.value)}
          helperText="Leave empty to use calculated profit from sells. Partners never see this."
          sx={{ maxWidth: 280 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save />}
            onClick={handleSaveOverride}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save override'}
          </Button>
          {hasOverride && (
            <Button size="small" onClick={handleClearOverride} disabled={saving}>
              Clear override
            </Button>
          )}
        </Stack>
        {hasOverride && (
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Override replaces the sell-based figure for admin views only.
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default PlanProfitOverrideSection;
